import type { PersonalizationPrimitive } from "./renderer";

export type ComparisonOperator =
  | "=="
  | "!="
  | ">="
  | "<="
  | ">"
  | "<"
  | "contains";

/** A condition in the template that compares a variable with a literal. */
export interface PreviewComparison {
  operator: ComparisonOperator;
  literal: PersonalizationPrimitive;
}

export interface PreviewVariable {
  name: string;
  /** Read inside a condition, directly or through a local variable. */
  isConditional: boolean;
  comparisons: PreviewComparison[];
}

/** AMPscript resolves names case-insensitively; Zephyr names are case-sensitive. */
export interface NameMatching {
  ignoreCase: boolean;
}

/**
 * Accumulates the variables a preview interpreter reads while it traces every
 * branch of a template. Names that the ESP treats as the same share one entry,
 * which keeps the spelling of its first read.
 */
export class PreviewVariableCollector {
  private readonly variables = new Map<string, PreviewVariable>();

  constructor(private readonly matching: NameMatching) {}

  read(name: string, isConditional = false): void {
    const variable = this.variable(name);
    if (isConditional) variable.isConditional = true;
  }

  compare(
    name: string,
    operator: ComparisonOperator,
    literal: PersonalizationPrimitive,
  ): void {
    const variable = this.variable(name);
    variable.isConditional = true;
    variable.comparisons.push({ operator, literal });
  }

  list(): PreviewVariable[] {
    return [...this.variables.values()];
  }

  private variable(name: string): PreviewVariable {
    const key = this.matching.ignoreCase ? name.toLowerCase() : name;
    let variable = this.variables.get(key);
    if (!variable) {
      variable = { name, isConditional: false, comparisons: [] };
      this.variables.set(key, variable);
    }
    return variable;
  }
}

export function lookupValue(
  values: Record<string, PersonalizationPrimitive>,
  name: string,
  { ignoreCase }: NameMatching,
): PersonalizationPrimitive | undefined {
  if (Object.hasOwn(values, name)) return values[name];
  if (!ignoreCase) return undefined;
  const lower = name.toLowerCase();
  const key = Object.keys(values).find((k) => k.toLowerCase() === lower);
  return key === undefined ? undefined : values[key];
}

/** Flips a comparison so the variable is always on the left. */
export function mirrorOperator(
  operator: ComparisonOperator,
): ComparisonOperator {
  switch (operator) {
    case ">":
      return "<";
    case "<":
      return ">";
    case ">=":
      return "<=";
    case "<=":
      return ">=";
    default:
      return operator;
  }
}

export function truthy(raw: unknown): boolean {
  if (typeof raw === "boolean") return raw;
  if (typeof raw === "number") return raw !== 0;
  if (typeof raw === "string") {
    const lower = raw.trim().toLowerCase();
    return lower !== "" && lower !== "false" && lower !== "0";
  }
  return raw != null;
}

/** Objects (rowsets, loop items) have no text form in a preview. */
export function toText(raw: unknown): string {
  if (
    typeof raw === "string" ||
    typeof raw === "number" ||
    typeof raw === "boolean"
  ) {
    return String(raw);
  }
  return "";
}

export function isNumeric(raw: unknown): boolean {
  if (typeof raw === "number") return Number.isFinite(raw);
  return (
    typeof raw === "string" && raw.trim() !== "" && Number.isFinite(Number(raw))
  );
}

export function toNumber(raw: unknown): number {
  const parsed = Number(toText(raw));
  return Number.isFinite(parsed) ? parsed : 0;
}

export function compareValues(
  left: unknown,
  right: unknown,
  operator: ComparisonOperator,
  { ignoreCase }: { ignoreCase: boolean },
): boolean {
  if (typeof left === "boolean" || typeof right === "boolean") {
    const equal = truthy(left) === truthy(right);
    if (operator === "==") return equal;
    if (operator === "!=") return !equal;
    return false;
  }
  const normalize = (raw: unknown) =>
    ignoreCase ? toText(raw).toLowerCase() : toText(raw);
  const [a, b]: [string | number, string | number] =
    isNumeric(left) && isNumeric(right)
      ? [toNumber(left), toNumber(right)]
      : [normalize(left), normalize(right)];
  switch (operator) {
    case "==":
      return a === b;
    case "!=":
      return a !== b;
    case ">":
      return a > b;
    case "<":
      return a < b;
    case ">=":
      return a >= b;
    case "<=":
      return a <= b;
    case "contains":
      return String(a).includes(String(b));
  }
}
